import {
  CheckCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import "./PasswordPolicyChecklist.css";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import {
  PASSWORD_POLICY_RULE_IDS,
  type PasswordPolicyResult,
  type PasswordPolicyRuleId,
} from "../../../queries/auth/passwordPolicyClient";

export type PasswordPolicyChecklistProps = {
  result: PasswordPolicyResult | null;
  isCurrent: boolean;
  isPending?: boolean;
  hasError?: boolean;
  showInlineError?: boolean;
};

const TEST_IDS: Record<PasswordPolicyRuleId, string> = {
  min_length: "req-length",
  requires_lowercase: "req-lowercase",
  requires_uppercase: "req-uppercase",
  requires_number: "req-number",
  requires_symbol: "req-symbol",
  not_email_similar: "req-email",
};

const MIN_PASSWORD_LENGTH = 14;

const getRuleLabel = (
  id: PasswordPolicyRuleId,
  result: PasswordPolicyResult | null,
) => {
  switch (id) {
    case "min_length":
      return formatString(AppStrings.PAM_PASSWORD_MIN_LENGTH, {
        minLength: String(MIN_PASSWORD_LENGTH),
      });
    case "requires_lowercase":
      return AppStrings.PAM_PASSWORD_LOWERCASE;
    case "requires_uppercase":
      return AppStrings.PAM_PASSWORD_UPPERCASE;
    case "requires_number":
      return AppStrings.PAM_PASSWORD_NUMBER;
    case "requires_symbol":
      return result
        ? formatString(AppStrings.PAM_PASSWORD_SYMBOL, {
            acceptedSymbols: result.accepted_symbols,
          })
        : AppStrings.PAM_PASSWORD_SYMBOL_PENDING;
    case "not_email_similar":
      return AppStrings.PAM_PASSWORD_EMAIL_SIMILARITY;
  }
};

const PasswordPolicyChecklist = ({
  result,
  isCurrent,
  isPending = false,
  hasError = false,
  showInlineError = false,
}: PasswordPolicyChecklistProps) => {
  const rulesById = new Map(result?.rules.map((rule) => [rule.id, rule]));

  return (
    <div className="password-policy-checklist">
      <p className="password-policy-checklist__title">
        {AppStrings.PAM_PASSWORD_REQUIREMENTS_TITLE}
      </p>
      <ul aria-live="polite">
        {PASSWORD_POLICY_RULE_IDS.map((id) => {
          const passed = Boolean(isCurrent && rulesById.get(id)?.passed);
          const state = isPending ? "pending" : passed ? "valid" : "invalid";
          return (
            <li
              key={id}
              data-testid={TEST_IDS[id]}
              className={state}
            >
              {isPending ? <LoadingOutlined /> : <CheckCircleOutlined />}
              <span>{getRuleLabel(id, result)}</span>
            </li>
          );
        })}
      </ul>
      {hasError && (
        <p className="password-policy-checklist__error" role="alert">
          {AppStrings.PAM_PASSWORD_POLICY_CHECK_FAILED}
        </p>
      )}
      {showInlineError && (
        <p className="password-policy-checklist__error" role="alert">
          {AppStrings.PAM_PASSWORD_REQUIREMENTS_NOT_MET}
        </p>
      )}
    </div>
  );
};

export default PasswordPolicyChecklist;
